import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, Sparkles, Check, RefreshCw } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function SvgToReact() {
  const [svgInput, setSvgInput] = useState(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="10" />
  <polyline points="12 6 12 12 16 14" />
</svg>`);
  const [reactOutput, setReactOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const [componentName, setComponentName] = useState("CustomIcon");
  
  // Custom generator configs
  const [options, setOptions] = useState({
    useTypeScript: true,
    passProps: true,
    inlineStyles: true,
    stripComments: true
  });

  const toggleOption = (key: keyof typeof options) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const convertSvgToReact = () => {
    if (!svgInput.trim()) {
      setReactOutput("");
      return;
    }

    let parsed = svgInput;

    // 1. Strip redundant declarations/comments
    parsed = parsed.replace(/<\?xml[^>]*\?>/gi, "");
    parsed = parsed.replace(/<!DOCTYPE[^>]*>/gi, "");
    if (options.stripComments) {
      parsed = parsed.replace(/<!--[\s\S]*?-->/g, "");
    }

    // 2. Map snake-case HTML/SVG attributes to standard React JSX camelCase properties
    const attributeMap: { [key: string]: string } = {
      "accent-height": "accentHeight",
      "alignment-baseline": "alignmentBaseline",
      "arabic-form": "arabicForm",
      "baseline-shift": "baselineShift",
      "cap-height": "capHeight",
      "clip-path": "clipPath",
      "clip-rule": "clipRule",
      "color-interpolation": "colorInterpolation",
      "color-interpolation-filters": "colorInterpolationFilters",
      "color-profile": "colorProfile",
      "color-rendering": "colorRendering",
      "dominant-baseline": "dominantBaseline",
      "enable-background": "enableBackground",
      "fill-opacity": "fillOpacity",
      "fill-rule": "fillRule",
      "flood-color": "floodColor",
      "flood-opacity": "floodOpacity",
      "font-family": "fontFamily",
      "font-size": "fontSize",
      "font-size-adjust": "fontSizeAdjust",
      "font-stretch": "fontStretch",
      "font-style": "fontStyle",
      "font-variant": "fontVariant",
      "font-weight": "fontWeight",
      "glyph-name": "glyphName",
      "glyph-orientation-horizontal": "glyphOrientationHorizontal",
      "glyph-orientation-vertical": "glyphOrientationVertical",
      "horiz-adv-x": "horizAdvX",
      "horiz-origin-x": "horizOriginX",
      "image-rendering": "imageRendering",
      "letter-spacing": "letterSpacing",
      "lighting-color": "lightingColor",
      "marker-end": "markerEnd",
      "marker-mid": "markerMid",
      "marker-start": "markerStart",
      "overline-position": "overlinePosition",
      "overline-thickness": "overlineThickness",
      "paint-order": "paintOrder",
      "panose-1": "panose1",
      "pointer-events": "pointerEvents",
      "rendering-intent": "renderingIntent",
      "shape-rendering": "shapeRendering",
      "stop-color": "stopColor",
      "stop-opacity": "stopOpacity",
      "strikethrough-position": "strikethroughPosition",
      "strikethrough-thickness": "strikethroughThickness",
      "stroke-dasharray": "strokeDasharray",
      "stroke-dashoffset": "strokeDashoffset",
      "stroke-linecap": "strokeLinecap",
      "stroke-linejoin": "strokeLinejoin",
      "stroke-miterlimit": "strokeMiterlimit",
      "stroke-opacity": "strokeOpacity",
      "stroke-width": "strokeWidth",
      "text-anchor": "textAnchor",
      "text-decoration": "textDecoration",
      "text-rendering": "textRendering",
      "underline-position": "underlinePosition",
      "underline-thickness": "underlineThickness",
      "unicode-bidi": "unicodeBidi",
      "unicode-range": "unicodeRange",
      "units-per-em": "unitsPerEm",
      "v-alphabetic": "vAlphabetic",
      "v-hanging": "vHanging",
      "v-ideographic": "vIdeographic",
      "v-mathematical": "vMathematical",
      "vector-effect": "vectorEffect",
      "vert-adv-y": "vertAdvY",
      "vert-origin-x": "vertOriginX",
      "vert-origin-y": "vertOriginY",
      "word-spacing": "wordSpacing",
      "writing-mode": "writingMode",
      "x-height": "xHeight",
      "xlink:href": "xlinkHref"
    };

    // Replace properties matching the snake-case keys
    Object.entries(attributeMap).forEach(([snake, camel]) => {
      const regex = new RegExp(`\\s${snake}=`, "g");
      parsed = parsed.replace(regex, ` ${camel}=`);
    });

    // 3. Remove style="string" and replace with React style={{}} structure
    if (options.inlineStyles) {
      parsed = parsed.replace(/style=["']([^"']*)["']/g, (match, styleStr) => {
        const rules = styleStr.split(";").filter((r: string) => r.trim());
        const styleObj = rules.map((rule: string) => {
          const parts = rule.split(":");
          if (parts.length < 2) return null;
          const key = parts[0].trim().replace(/-([a-z])/g, (g) => g[1].toUpperCase());
          const val = parts.slice(1).join(":").trim();
          return `${key}: "${val}"`;
        }).filter(Boolean).join(", ");
        return `style={{ ${styleObj} }}`;
      });
    }

    // 4. Handle props spreading on outer <svg> tag
    if (options.passProps) {
      parsed = parsed.replace(/<svg\b/i, "<svg {...props}");
    }

    // 5. Structure React Functional Component code templates
    let componentBody = "";
    const cleanName = componentName.trim().replace(/[^a-zA-Z0-9]/g, "") || "CustomIcon";

    if (options.useTypeScript) {
      componentBody = `import React from 'react';

interface ${cleanName}Props extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
}

export function ${cleanName}(${options.passProps ? `{ size = 24, ...props }: ${cleanName}Props` : ""}) {
  return (
    ${parsed.trim().replace(/<svg/i, `<svg width={size} height={size}`)}
  );
}`;
    } else {
      componentBody = `import React from 'react';

export function ${cleanName}(${options.passProps ? `{ size = 24, ...props }` : ""}) {
  return (
    ${parsed.trim().replace(/<svg/i, `<svg width={size} height={size}`)}
  );
}`;
    }

    setReactOutput(componentBody);
  };

  useEffect(() => {
    convertSvgToReact();
  }, [svgInput, componentName, options]);

  const handleCopy = () => {
    copyToClipboard(reactOutput).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="SVG to React Component Converter - Convert SVG Vector to JSX React"
      description="Convert raw SVG vector graphics into high-quality, reusable, typed React Functional Components (TSX/JSX) with custom prop spreading and camelCase support."
      h1="SVG to React"
      category="SVG Tools"
      categoryPath="/svg-tools"
      howToUse={[
        "Enter or paste your raw SVG vector markup into the input panel.",
        "Optionally customize the generated React component name (e.g. NavigationIcon).",
        "Configure compilation toggles (TypeScript interfaces, spread props, style structures).",
        "Copy your optimized TSX or JSX component and paste it straight into your source code."
      ]}
      faq={[
        { question: "What does an online SVG to React converter do?", answer: "An online SVG to React converter translates standard XML-based SVG strings into clean, modular, modern React Functional Components (with JSX/TSX coordinates) suitable for immediate web application deployment." },
        { question: "Why must SVG attributes change when converting to React components?", answer: "React uses Javascript Syntax Extension (JSX), where standard HTML/XML snake-case attributes like 'stroke-width' or 'fill-rule' conflict with standard Javascript syntax. JSX requires camelCase replacements like 'strokeWidth' and 'fillRule'." },
        { question: "Does this converter support TypeScript interfaces?", answer: "Yes, toggling the 'Use TypeScript' setting automatically generates detailed React component TSX code with custom interface structures, supporting standard SVG prop typings." },
        { question: "Is my proprietary icon code secure on this online utility?", answer: "Absolutely. All attribute parsing, component templating, and camelCase conversion logic execute locally inside your client-side browser runtime, safeguarding files from leaking." },
        { question: "What is the benefit of passing props to React SVG components?", answer: "Spreading `{...props}` on the SVG component allows developers to dynamically override properties (like color fill, strokes, margins, click listeners, and animations) dynamically from parent components." },
        { question: "How are inline style attributes formatted for React elements?", answer: "Standard styles like 'style=\"fill:red;stroke:blue\"' are parsed and mapped to React’s native Javascript style objects: `style={{ fill: 'red', stroke: 'blue' }}`." },
        { question: "Can I use the converted components in React Native?", answer: "Partially. React Native utilizes a different vector engine library (like `react-native-svg`), requiring element mapping. This JSX component is highly tailored for standard React Web, Next.js, and Gatsby." },
        { question: "How do I import the generated component into my code files?", answer: "Save the output code as a file (e.g., `CustomIcon.tsx`) inside your components folder, and import it: `import { CustomIcon } from './components/CustomIcon';`." },
        { question: "Does the converter preserve animation CSS within style tags?", answer: "Yes. CSS definitions are fully preserved within safe React structure arrays, maintaining SMIL structures and dynamic hover keyframe logic." },
        { question: "Why is utilizing React SVG components superior to using standard img paths?", answer: "Inline SVG components render directly in the HTML DOM, loading instantly without triggering additional HTTP asset request overhead. They can be styled dynamically via Tailwind CSS classes." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Controls Column */}
          <div className="space-y-5 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">Generator Options</span>
            </div>

            {/* Custom Component Name */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">React Component Name</Label>
              <Input
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
                placeholder="e.g. HamburgerIcon"
                className="text-xs"
              />
            </div>

            {/* Controls Checklist */}
            <div className="space-y-3.5 pt-1">
              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.useTypeScript}
                  onChange={() => toggleOption("useTypeScript")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Use TypeScript (TSX Components)
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.passProps}
                  onChange={() => toggleOption("passProps")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Spread custom props {"(<svg {...props}>)"}
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.inlineStyles}
                  onChange={() => toggleOption("inlineStyles")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Convert style string to react style object
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.stripComments}
                  onChange={() => toggleOption("stripComments")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Strip source design comments
              </label>
            </div>
          </div>

          {/* Editors Grid Column */}
          <div className="md:col-span-2 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Source SVG Code</Label>
                <Textarea
                  value={svgInput}
                  onChange={(e) => setSvgInput(e.target.value)}
                  className="font-mono text-[10px] leading-relaxed min-h-[320px] bg-slate-50"
                  placeholder="Paste your standard <svg> raw tags here..."
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">React Functional Component</Label>
                <Textarea
                  value={reactOutput}
                  readOnly
                  className="font-mono text-[10px] leading-relaxed min-h-[320px] bg-white border-blue-200 focus-visible:ring-blue-500"
                  placeholder="React component files render automatically..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex">
              <Button
                onClick={handleCopy}
                disabled={!reactOutput}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? "Copied React Component!" : "Copy Generated React Component Code"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
